import React, { useState } from 'react';
import { StyleSheet, Text, View, LayoutChangeEvent } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingVerticalScrollView } from './webScrollStyles';

export interface KCPHGroupedRow {
  label: string;
  estimated: number;
  scheduled: number;
  actual: number;
}

interface KCPHPerformanceCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  rows: KCPHGroupedRow[];
  /** Prior-period rows (same labels) used to render a "vs prior" delta next to Actual KCPH. */
  priorRows?: KCPHGroupedRow[];
  /** Single KCPH target for the whole scope — drives both the target line and its legend label. */
  target?: number;
}

const SERIES = [
  { key: 'estimated', label: 'Estimated KCPH', color: '#3FAED8' },
  { key: 'scheduled', label: 'Scheduled KCPH', color: '#7A5AF8' },
  { key: 'actual', label: 'Actual KCPH', color: '#FF8A00' },
];
const BAR_HEIGHT = 10;
const GROUP_GAP = 3;
// Must match rowLabelWrap's width + rowGroup's columnGap below — the target line spans the
// full chart height so its offset is computed against this reserved label column, not the
// bars column's own width, since it isn't nested inside barsContainer.
const ROW_LABEL_WIDTH = 98;
const ROW_GROUP_GAP = 10;
const CHART_AREA_PADDING_RIGHT = 8;

const KCPHPerformanceCard: React.FC<KCPHPerformanceCardProps> = ({
  title,
  subtitle,
  description,
  rows,
  priorRows = [],
  target = 90,
}) => {
  const [chartAreaWidth, setChartAreaWidth] = useState(0);
  const onChartAreaLayout = (event: LayoutChangeEvent) => {
    const nextWidth = event.nativeEvent.layout.width;
    if (nextWidth > 0 && Math.abs(nextWidth - chartAreaWidth) > 1) setChartAreaWidth(nextWidth);
  };

  // Check for no data condition
  if (!rows || rows.length === 0) {
    return (
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>{title}</Text>
          {description && <Text style={styles.description}>{description}</Text>}
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No KCPH performance data available</Text>
        </View>
      </View>
    );
  }


  // Calculate dynamic max based on data
  const allValues = rows
    .flatMap((row) => SERIES.map((series) => row[series.key as keyof KCPHGroupedRow]))
    .filter((v) => typeof v === 'number' && !isNaN(v));
  
  const dataMax = Math.max(target, ...(allValues.length > 0 ? allValues : [140]));
  const MAX_KCPH = Math.ceil(dataMax * 1.15); // Add 15% padding above max value
  
  const barWidth = (value: number) => (value / MAX_KCPH) * 80 + '%';

  const priorByLabel = new Map(priorRows.map((row) => [row.label, row]));

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>{title}</Text>
          {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>
        {description && <Text style={styles.description}>{description}</Text>}
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        {SERIES.map((series) => (
          <View key={series.key} style={styles.legendItem}>
            <View style={[styles.legendSwatch, { backgroundColor: series.color }]} />
            <Text style={styles.legendText}>{series.label}</Text>
          </View>
        ))}
        <View style={styles.legendItem}>
          <View style={styles.legendTargetLine} />
          <Text style={styles.legendText}>{`Target ${target} KCPH`}</Text>
        </View>
      </View>

      {/* Chart */}
      <View style={styles.chartViewport}>
        <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
          <View style={styles.chartArea} onLayout={onChartAreaLayout}>
            {rows.map((row, rowIndex) => {
              const priorRow = priorByLabel.get(row.label);
              const actualDelta = priorRow ? row.actual - priorRow.actual : undefined;
              return (
              <View
                key={row.label}
                style={[styles.rowGroup, rowIndex < rows.length - 1 && styles.rowGroupDivider]}
              >
                <View style={styles.rowLabelWrap}>
                  <Text style={styles.rowLabel}>{row.label}</Text>
                  {actualDelta != null && !isNaN(actualDelta) && Math.abs(actualDelta) >= 0.05 && (
                    <Text style={[styles.rowDelta, actualDelta > 0 ? styles.rowDeltaUp : styles.rowDeltaDown]}>
                      {`${actualDelta > 0 ? '▲' : '▼'}${Math.abs(actualDelta).toFixed(1)} vs prior`}
                    </Text>
                  )}
                </View>

                <View style={styles.barsContainer}>
                  {SERIES.map((series, seriesIndex) => {
                    const value = row[series.key as keyof KCPHGroupedRow];
                    return (
                      <View
                        key={`${row.label}-${series.key}`}
                        style={[
                          styles.bar,
                          {
                            width: barWidth(value),
                            backgroundColor: series.color,
                            marginBottom: seriesIndex < SERIES.length - 1 ? GROUP_GAP : 0,
                          },
                        ]}
                      >
                        <Text style={styles.barValue}>{value.toFixed(0)}</Text>
                      </View>
                    );
                  })}
                </View>
              </View>
              );
            })}

            {/* Target Line — positioned against the bars column's own width, offset past the
                fixed-width row label column, so it never overlaps the location names. */}
            {chartAreaWidth > 0 && (() => {
              const barsAreaWidth = Math.max(0, chartAreaWidth - CHART_AREA_PADDING_RIGHT - ROW_LABEL_WIDTH - ROW_GROUP_GAP);
              const targetLeft = ROW_LABEL_WIDTH + ROW_GROUP_GAP + (target / MAX_KCPH) * barsAreaWidth;
              return (
                <View style={[styles.targetLine, { left: targetLeft }]} />
              );
            })()}
          </View>
        </CrewingVerticalScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    flex: 1,
    minHeight: 360,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
    width: '100%',
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    minWidth: 150,
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  description: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
    textAlign: 'right',
    flexShrink: 1,
    maxWidth: '100%',
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  legendTargetLine: {
    width: 12,
    height: 0,
    borderTopWidth: 2,
    borderTopColor: '#7A7F86',
    borderStyle: 'dotted',
  },
  legendText: {
    fontSize: 11,
    color: '#6B7280',
  },
  chartViewport: {
    flex: 1,
    marginVertical: 12,
  },
  chartArea: {
    paddingRight: 8,
    position: 'relative',
    width: '100%',
  },
  rowGroup: {
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 10,
  },
  rowGroupDivider: {
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F7',
  },
  rowLabelWrap: {
    width: 98,
  },
  rowLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  rowDelta: {
    marginTop: 2,
    fontSize: 9,
    fontWeight: '600',
  },
  rowDeltaUp: {
    color: '#0B5FC6',
  },
  rowDeltaDown: {
    color: '#C12C01',
  },
  barsContainer: {
    flex: 1,
    gap: 0,
  },
  bar: {
    height: BAR_HEIGHT,
    backgroundColor: '#0B5FC6',
    borderRadius: 3,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  barValue: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  targetLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 0,
    borderLeftWidth: 2,
    borderLeftColor: '#7A7F86',
    borderStyle: 'dotted',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontWeight: '500',
  },
});

export default KCPHPerformanceCard;
